import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronRight, Clock, ArrowLeft } from "lucide-react";
import { usePageMeta } from "@/components/site/usePageMeta";
import { ContactSection } from "@/components/site/ContactSection";
import { Btn } from "@/components/site/ui";
import { getPost, POSTS } from "@/components/site/blogData";
import { ROUTES } from "@/components/site/data";

// Renders a paragraph of plain text, converting any migrated internal links
// (already normalized to /blog/{slug} in blogData.js) into real <Link>s.
function renderTextWithLinks(text, links) {
  if (!links || links.length === 0) return text;
  let parts = [text];
  links.forEach((link, linkIndex) => {
    if (!link.text || !link.href || !link.href.startsWith("/")) return;
    const next = [];
    parts.forEach((part) => {
      if (typeof part !== "string") {
        next.push(part);
        return;
      }
      const idx = part.indexOf(link.text);
      if (idx === -1) {
        next.push(part);
        return;
      }
      if (idx > 0) next.push(part.slice(0, idx));
      next.push(
        <Link
          key={`link-${linkIndex}-${idx}`}
          to={link.href}
          className="text-[#254369] underline decoration-[#C9A961] hover:text-[#C9A961]"
        >
          {link.text}
        </Link>
      );
      const rest = part.slice(idx + link.text.length);
      if (rest) next.push(rest);
    });
    parts = next;
  });
  return parts;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPost(slug);
  usePageMeta(
    post ? post.metaTitle || `${post.title} | Wallstreet Realty Blog` : "Blog | Wallstreet Realty",
    post ? post.metaDescription || post.excerpt : undefined
  );
  if (!post) return <Navigate to={ROUTES.blog} replace />;

  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <>
      <article className="pt-[70px]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <nav className="flex items-center gap-1.5 text-xs text-[#2D2D37]/50 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#254369]">Home</Link>
            <ChevronRight size={13} />
            <Link to={ROUTES.blog} className="hover:text-[#254369]">Blog</Link>
            <ChevronRight size={13} />
            <span className="text-[#254369] font-medium truncate max-w-[200px]">{post.category}</span>
          </nav>

          <div className="flex items-center gap-3 text-xs">
            <span className="text-[#C9A961] font-heading font-bold uppercase tracking-[0.15em]">{post.category}</span>
            <span className="inline-flex items-center gap-1 text-[#2D2D37]/45">
              <Clock size={12} /> {post.readMins} min read
            </span>
          </div>
          <h1 className="mt-4 font-heading font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.1] text-[#2D2D37]">
            {post.title}
          </h1>

          {(post.author || post.publishDate) && (
            <p className="mt-3 text-xs text-[#2D2D37]/50">
              {post.author && <span>By {post.author}</span>}
              {post.author && post.publishDate && <span> · </span>}
              {post.publishDate && <span>{post.publishDate}</span>}
            </p>
          )}

          {post.categories && post.categories.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {post.categories.map((c) => (
                <span
                  key={c}
                  className="text-[10px] uppercase tracking-wide bg-[#F5F5F5] border border-[#E5E5E5] rounded-full px-2.5 py-1 text-[#2D2D37]/60"
                >
                  {c}
                </span>
              ))}
            </div>
          )}

          <img
            src={post.image}
            alt={post.imageAlt || post.title}
            className="mt-8 w-full h-[260px] sm:h-[380px] object-cover rounded-[20px]"
            loading="eager"
          />

          <div className="mt-10 space-y-6">
            {post.body.map((block, i) => (
              <div key={i}>
                {block.h && (
                  <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#2D2D37] mt-10 mb-3">{block.h}</h2>
                )}
                {block.p && (
                  <p className="text-[16px] leading-[1.8] text-[#2D2D37]/75">
                    {renderTextWithLinks(block.p, post.internalLinks)}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 bg-[#F5F5F5] border border-[#E5E5E5] rounded-[20px] p-8 text-center">
            <p className="font-heading font-bold text-xl text-[#2D2D37]">Have a question about your situation?</p>
            <p className="mt-2 text-sm text-[#2D2D37]/65">Talk to the Wallstreet Realty team — real answers, no pressure.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Btn href={ROUTES.contact} testid="post-cta-contact">Contact Us</Btn>
              <Btn href={ROUTES.idx} variant="gold" testid="post-cta-search">Search Homes</Btn>
            </div>
          </div>

          <div className="mt-12">
            <Link to={ROUTES.blog} className="inline-flex items-center gap-2 text-sm font-medium text-[#254369] hover:text-[#C9A961] transition-colors">
              <ArrowLeft size={15} /> All Articles
            </Link>
            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`${ROUTES.blog}/${r.slug}`}
                  className="lift group bg-white border border-[#E5E5E5] rounded-[20px] p-6 hover:border-[#C9A961]"
                >
                  <span className="text-[#C9A961] font-heading font-bold uppercase tracking-[0.15em] text-xs">{r.category}</span>
                  <h3 className="mt-2 font-heading font-bold text-[16px] leading-snug text-[#2D2D37] group-hover:text-[#254369]">
                    {r.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
      <ContactSection />
    </>
  );
}
